import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { useRootStore } from "../../../../store/contexts";
import { ShowReferenceSet } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowIntegerChoices } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { FormControl } from "@mui/material";

export type EditionDocumentationViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  worksValues: { displayId: string; id: string }[];
  authorsDisplayValue?: { displayId: string; id: string } | null;
  authorsDisplay: string;
  publisherConfigurationDisplayValue?: { displayId: string; id: string } | null;
  publisherConfigurationDisplay: string;
  territoriesDisplayValue?: { displayId: string; id: string } | null;
  territoriesDisplay: string;
  contractDurationValue?: { displayId: string; id: string } | null;
  contractDuration: number | undefined;
  contractRenewalValue?: { displayId: string; id: string } | null;
  contractRenewal: number | undefined;
  contractNumberValue?: { displayId: string; id: string } | null;
  contractNumber: string;
  documentNameValue?: { displayId: string; id: string } | null;
  documentName: string;
  finalDocumentPdfFiles: CrudRows;
  finalDocumentPdfViewAction?: IMenuAction[];
};

export const EditionDocumentationViewForm: React.FC<EditionDocumentationViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      worksValues,
      authorsDisplay = "",
      publisherConfigurationDisplay = "",
      territoriesDisplay = "",
      contractDuration = undefined,
      contractRenewal = undefined,
      contractNumber = "",
      documentName = "",
      finalDocumentPdfFiles,
      finalDocumentPdfViewAction,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupEditionDocumentationLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowReferenceSet
                  label={langStore.EditionDocumentationWorksLabel}
                  selectedValues={worksValues}
                />

                <ShowMultiLine
                  value={authorsDisplay}
                  label={langStore.EditionDocumentationAuthorsDisplayLabel}
                />

                <ShowMultiLine
                  value={publisherConfigurationDisplay}
                  label={
                    langStore.EditionDocumentationPublisherConfigurationDisplayLabel
                  }
                />

                <ShowMultiLine
                  value={territoriesDisplay}
                  label={langStore.EditionDocumentationTerritoriesDisplayLabel}
                />
                <ShowIntegerChoices
                  value={contractDuration}
                  label={langStore.EditionDocumentationContractDurationLabel}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  nullText={
                    langStore.EditionDocumentationContractDurationNullText
                  }
                />
                <ShowIntegerChoices
                  value={contractRenewal}
                  label={langStore.EditionDocumentationContractRenewalLabel}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  nullText={
                    langStore.EditionDocumentationContractRenewalNullText
                  }
                />
                <ShowMultiLine
                  value={contractNumber}
                  label={langStore.EditionDocumentationContractNumberLabel}
                />
                <ShowMultiLine
                  value={documentName}
                  label={langStore.EditionDocumentationDocumentNameLabel}
                />

                <ShowFilesCompact
                  rows={finalDocumentPdfFiles}
                  showLabel={true}
                  label={langStore.EditionDocumentationFinalDocumentPdfLabel}
                  menuActions={finalDocumentPdfViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
